import React, { useState, useEffect } from "react";
import "../styles/confirmation-dialog.css";

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: "danger" | "warning" | "info";
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  type = "info",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsAnimating(false);
    } else if (isVisible) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsAnimating(false);
      }, 300); // Match the CSS animation duration
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible]);

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onCancel();
    } else if (e.key === "Enter") {
      onConfirm();
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={`confirmation-dialog-overlay ${isAnimating ? "fade-out" : ""}`}
      onClick={handleBackdropClick}
    >
      <div
        className={`confirmation-dialog ${type} ${
          isAnimating ? "slide-out" : ""
        }`}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
        role="dialog"
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-message"
      >
        <div className="confirmation-dialog-header">
          <h3
            id="confirmation-dialog-title"
            className="confirmation-dialog-title"
          >
            {title}
          </h3>
        </div>

        <div className="confirmation-dialog-body">
          <p
            id="confirmation-dialog-message"
            className="confirmation-dialog-message"
          >
            {message}
          </p>
        </div>

        <div className="confirmation-dialog-footer">
          <button
            type="button"
            className="confirmation-dialog-btn confirmation-dialog-btn-cancel"
            onClick={handleCancel}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={`confirmation-dialog-btn confirmation-dialog-btn-confirm ${type}`}
            onClick={handleConfirm}
            autoFocus
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
